import { apiService } from './api.js';

/**
 * WhatsApp Configuration Service
 * Handles all WhatsApp configuration related operations
 */
class WhatsAppConfigService {
  /**
   * Get WhatsApp configuration for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} WhatsApp configuration data
   */
  async getWhatsAppConfig(projectId) {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await apiService.get(`/whatsapp-config?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
      throw error;
    }
  }

  /**
   * Create new WhatsApp configuration
   * @param {string} projectId - Project ID
   * @param {Object} configData - Configuration data
   * @param {string} configData.accessToken - Facebook access token
   * @param {string} configData.whatsappBusinessAccountId - WhatsApp Business Account ID
   * @param {string} configData.phoneNumberId - Phone Number ID
   * @param {string} configData.phoneNumber - Phone number
   * @param {string} configData.businessId - Business ID
   * @returns {Promise<Object>} Created configuration data
   */
  async createWhatsAppConfig(projectId, configData) {
    try {
      const response = await apiService.post('/whatsapp-config', {
        projectId,
        ...configData,
      });
      return response;
    } catch (error) {
      console.error('Error creating WhatsApp config:', error);
      throw error;
    }
  }

  /**
   * Update existing WhatsApp configuration
   * @param {string} projectId - Project ID
   * @param {Object} updateData - Updated configuration data
   * @returns {Promise<Object>} Updated configuration data
   */
  async updateWhatsAppConfig(projectId, updateData) {
    try {
      const response = await apiService.put('/whatsapp-config', {
        projectId,
        ...updateData,
      });
      return response;
    } catch (error) {
      console.error('Error updating WhatsApp config:', error);
      throw error;
    }
  }

  /**
   * Delete WhatsApp configuration
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteWhatsAppConfig(projectId) {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await apiService.delete(`/whatsapp-config?${params}`);
      return response;
    } catch (error) {
      console.error('Error deleting WhatsApp config:', error);
      throw error;
    }
  }

  /**
   * Test WhatsApp connection
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Test result
   */
  async testWhatsAppConnection(projectId) {
    try {
      const response = await apiService.post(
        `/whatsapp-config/${projectId}/test`
      );
      return response;
    } catch (error) {
      console.error('Error testing WhatsApp connection:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const whatsappConfigService = new WhatsAppConfigService();
